import { sha256 } from "js-sha256";
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { getCookie, isDebugAPI, logErrorToSentry } from "@litres/utils";
import type {
  ICat2Request,
  ICat2Response,
  IGlobalApiParams,
  IReduxRequestNode,
  IResponse,
  IResponseErrorDetails,
} from "@litres-libs/types";
import axios from "../../instance/axios";
import type { ICat2Config } from "../config/cat2";
import { cat2Config } from "../config/cat2";
import { ApiError, ApiType } from "../../types/api";

/**
 * Провайдер запросов в каталит2.
 * Создает синглтон, чтобы все запросы шли через 1 точку
 */
export class Catalit2PromiseProvider<TParams, TPayload> {
  private constructor(config: ICat2Config, initialSID: string | undefined) {
    this.SID = initialSID || undefined;
    this.baseURL = config.baseURL || "DEFAULT_BASE_URL";
    this.appID = config.appID || "DEFAULT_APP_ID";
    this.secretKey = config.secretKey || "DEFAULT_APP_KEY";
  }

  private SID: string | undefined;

  private baseURL: string;

  private appID: string;

  private secretKey: string;

  private static instance: Catalit2PromiseProvider<any, any>;

  /**
   * Метод, возвращающий единичный экземпляр синглтона
   * @returns Возвращает провайдер каталита для вызова запросов
   */
  public static getInstance<TParams, TPayload>(): Catalit2PromiseProvider<
    TParams,
    TPayload
  > {
    if (!Catalit2PromiseProvider.instance) {
      // Передачу конфига не делаем, рассчитываем что мы работаем с одним каталитом по одному конфигу
      // По умолчанию берем SID из куки, но в дальнейшем дадим делать запрос от сида в параметрах запроса
      const initialSID = getCookie("SID");
      Catalit2PromiseProvider.instance = new Catalit2PromiseProvider<
        TParams,
        TPayload
      >(cat2Config, initialSID);
    }

    return Catalit2PromiseProvider.instance;
  }

  private makeCatalitRequestItem(
    params: IGlobalApiParams<TParams>
  ): ICat2Request<TParams> {
    // Нам надо получить нечто такое
    // {"func":"r_last_search","id":"R_LAST_SEARCH","param":{"limit":[0,20]}}

    return {
      func: params.provider.func || params.provider.id || "UNDEFINED_FUNC",
      id: params.provider.id || "UNDEFINED_ID",
      param: params.request?.params ? params.request.params : undefined,
    };
  }

  /**
   * Основной метод вызова запроса
   * @param params - стандартные передаваемые параметры для всех запросов в любые наши API
   * @returns {Promise} - Возвращает пропис запроса каталита
   */
  public async makeAxiosRequest(
    params: IGlobalApiParams<TParams>,
    config: AxiosRequestConfig = {}
  ): Promise<IReduxRequestNode<TParams, TPayload>> {
    // Каталит работает на передаче FormData, включающей массив requests(сами запросы) и авторизационные данные
    // FormData передается обернутой в обхект "jdata"
    const bodyFormData = new FormData();

    const request = this.makeCatalitRequestItem(params);

    if (params.file) {
      bodyFormData.append("upload_file", params.file);
      request.param = {
        ...(request.param || {}),
        upload_file: "upload_file",
      } as TParams;
    }

    bodyFormData.append(
      "jdata",
      JSON.stringify({
        sid: this.SID,
        time: this.getCurrentTime(),
        requests: [request],
        mobile_app: this.appID,
        sha: this.generateSha256(),
      })
    );

    const url =
      params.url && params.url !== "/"
        ? this.baseURL + params.url
        : this.baseURL;
    const method = params.request ? params.request.method : "POST";

    // Делаем запрос
    const res = axios({
      url,
      method,
      data: bodyFormData,
      timeout: 40000,
      headers: {
        "Content-Type": "multipart/form-data;",
      },
      ...config,
    })
      .then((response: AxiosResponse<ICat2Response<TPayload>>) => {
        // Если каталит ответил нам ошибкой - выкинем ошибку в catch для обработки как ошибки
        // Нам нужно передать это как error.response, чтобы catch спарсил аналогично ошибке
        // Данная ошибка произойдет если каталит вообще не принял запрос (глобальная ошибка каталита)
        if (response.data.error_code || response.data.error_message) {
          throw {
            request: params,
            response: {
              status: response.data.error_code,
              statusText: response.data.error_message,
            },
          } as unknown as Error;
        }

        // Если каталит ответил ошибкой на переданном ID функции (ошибка конкретного запроса с кодом ответа ошибки каталита)
        // В нашем случае мы не запрашиваем сразу несколько функций, поэтому всегда придет один
        if (params.provider.id && response.data[params.provider.id]) {
          const functionResponse = response.data[params.provider.id];

          if (functionResponse?.success !== true) {
            throw {
              request: params,
              response: {
                status: response.data[params.provider.id]?.error_code,
                statusText: response.data[params.provider.id]?.error_message,
              },
            } as unknown as Error;
          }
        }

        if (isDebugAPI) {
          console.info(
            `%cRES: ${params.provider.id}`,
            "background: #abff5f; padding: 2px 12px; color: #848484;",
            response.data[params.provider.id],
            params
          );
        }

        if (params.onSuccess) {
          params.onSuccess({
            request: params,
            response: response.data[params.provider.id],
          });
        }

        delete params.onError;
        delete params.onSuccess;

        // В случае успешного ответа вернем ответ в fullfill
        return {
          request: params,
          response: response.data[params.provider.id],
        };
      })
      .catch((error: AxiosError<IResponse>) => {
        if (error.response?.status === 500) {
          logErrorToSentry(new ApiError("API Error (500)"), {
            contextProperties: [
              "REQUEST_PARAMS",
              {
                url,
                method,
                data: JSON.stringify(bodyFormData),
              },
            ],
            tags: {
              api_name: ApiType.Cat2,
            },
          });
        }

        if (isDebugAPI) {
          console.error(
            `%cERR: ${params.provider.id}`,
            "background: #f55e5e; padding: 2px 12px; color: #fff",
            error
          );
        }

        if (params.onError) {
          params.onError({
            request: params,
            error: {
              status: error.response?.status,
              type: (error.response?.data.error as IResponseErrorDetails).type,
              title: (error.response?.data.error as IResponseErrorDetails)
                .title,
            },
          });
        }

        delete params.onError;
        delete params.onSuccess;

        if (error.response?.status && error.response?.statusText) {
          return {
            request: params,
            error: {
              status: error.response.status,
              statusText: error.response?.statusText,
            },
          };
        }

        return {
          request: params,
          error: {
            status: error.response?.status,
            type: (error.response?.data.error as IResponseErrorDetails).type,
            title: (error.response?.data.error as IResponseErrorDetails).title,
          },
        };
      });

    return res as Promise<IReduxRequestNode<TParams, TPayload>>;
  }

  /**
   * Метод позволяет получить строку с датой с учетом UTC для подписи запроса каталита
   * @returns {string} - Строка с датой (с учетом таймзон)
   */
  private getCurrentTime(): string {
    const currentDate = new Date();
    const timezoneOffset = -currentDate.getTimezoneOffset();
    const dif = timezoneOffset >= 0 ? "+" : "-";

    const pad = (num: number): string => {
      const norm = Math.floor(Math.abs(num));
      return (norm < 10 ? "0" : "") + String(norm);
    };

    return `${currentDate.getFullYear()}-${pad(
      currentDate.getMonth() + 1
    )}-${pad(currentDate.getDate())}T${pad(currentDate.getHours())}:${pad(
      currentDate.getMinutes()
    )}:${pad(currentDate.getSeconds())}${dif}${pad(timezoneOffset / 60)}:${pad(
      timezoneOffset % 60
    )}`.toString();
  }

  /**
   * Метод позволяет получить SHA256 на основе даты и secretKey приложения
   * @returns {string} - возвращает sha256
   */
  private generateSha256(): string {
    const result = this.getCurrentTime() + this.secretKey;
    return sha256(result).toString();
  }
}
